import { useEffect, useState } from 'react'

import mixpanel from 'mixpanel-browser'
import * as THREE from 'three'

import { useBuildingContext } from '../../../../../../../contexts/buildingContext'
import { LoadingIndicator } from '../../../../../../common/LoadingIndicator'
import { ForgeViewer } from '../../../../../../third_party/ForgeViewer'
import { useImageViewerContext } from '../../../../imageViewerContext'
import { ViewerPosition } from '../../../../types'

const CAMERA_HEIGHT = 4

interface Transformation {
  linear_transformation: [number, number][]
  translation: [number, number]
  scaling: [number, number]
  rotation: number
}

interface FloorTranslation {
  height: number
  primary_to_secondary: string
  secondary_to_primary: string
}

interface XRayViewProps {
  viewerPosition: ViewerPosition
  viewerPositionDelta?: ViewerPosition
  followRealtime?: boolean
  onDragging?: (isActive: boolean) => void
  onUpdate?: (newValues: ViewerPosition) => void
  onRequestCameraScreenshot?: (callback: (blob: Blob) => void) => void
}

export const XRayView = ({
  viewerPosition,
  viewerPositionDelta,
  followRealtime = false,
  onDragging,
  onUpdate,
  onRequestCameraScreenshot,
}: XRayViewProps) => {
  const { state: buildingState } = useBuildingContext()
  const { state: imageViewerContext } = useImageViewerContext()
  const [position, setPosition] = useState<THREE.Vector3>()
  const [loading, setLoading] = useState<boolean>(true)
  const [rotation, setRotation] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const transformPoint = (
    x: number,
    y: number,
    transformations: Transformation,
  ): THREE.Vector2 => {
    const [[a, b], [c, d]] = transformations.linear_transformation
    const [tx, ty] = transformations.translation

    const transformedX = a * x + b * y + tx
    const transformedY = c * x + d * y + ty

    return new THREE.Vector2(transformedX, transformedY)
  }

  const handleLoaded = (loaded: boolean) => {
    setLoading(!loaded)
  }

  useEffect(() => {
    setError(null)

    // Check if required data exists
    if (!buildingState.projectData?.forge_translations_json) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.warn(
        'XRayView: forge_translations_json is missing from projectData',
      )
      setError('3D model transformation data is not available for this project')
      setLoading(false)
      return
    }

    if (!buildingState.floorData?.floor_code) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.warn('XRayView: floor_code is missing from floorData')
      setError('Floor code is not available')
      setLoading(false)
      return
    }

    if (
      !buildingState.pointData?.x ||
      buildingState.pointData?.y === undefined
    ) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.warn('XRayView: pointData x or y is missing')
      setError('Point coordinates are not available')
      setLoading(false)
      return
    }

    const forgeJSON: Record<string, FloorTranslation> =
      buildingState.projectData.forge_translations_json

    const floorTransformation = forgeJSON[buildingState.floorData.floor_code]

    if (!floorTransformation) {
      const availableCodes = Object.keys(forgeJSON)
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.warn(
        `XRayView: No floor transformation found for floor_code: ${buildingState.floorData.floor_code}`,
        'Available floor codes:',
        availableCodes,
      )
      setError(
        `3D transformation not configured for floor: ${buildingState.floorData.floor_code}`,
      )
      setLoading(false)
      return
    }

    if (!floorTransformation.primary_to_secondary) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.warn(
        'XRayView: primary_to_secondary is missing from floorTransformation',
      )
      setError('Floor transformation data is incomplete')
      setLoading(false)
      return
    }

    let transformations: Transformation
    try {
      transformations = JSON.parse(floorTransformation.primary_to_secondary)
    } catch (error) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.error(
        'XRayView: Failed to parse primary_to_secondary JSON:',
        error,
      )
      setError('Invalid transformation data format')
      setLoading(false)
      return
    }

    const x = buildingState.pointData.x
    const y = buildingState.pointData.y

    const transformedPosition = transformPoint(x, y, transformations)

    const calculatedPosition = new THREE.Vector3(
      transformedPosition.x,
      transformedPosition.y,
      floorTransformation.height + CAMERA_HEIGHT,
    )

    setRotation(transformations.rotation || 0)
    setPosition(calculatedPosition)
    setError(null)

    mixpanel.track('Show 3D Viewer')
  }, [buildingState])

  // Show error message if data is missing
  if (error) {
    return (
      <div
        style={{
          background: '#f8f8f8',
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          padding: '20px',
          color: '#666',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <h3 style={{ marginBottom: '10px' }}>3D Viewer Unavailable</h3>
          <p>{error}</p>
          <p style={{ fontSize: '12px', marginTop: '10px', color: '#999' }}>
            This may be expected for projects without 3D model configuration.
          </p>
        </div>
      </div>
    )
  }

  if (!position) {
    return (
      <div style={{ background: '#f8f8f8', height: '100%', width: '100%' }}>
        <LoadingIndicator />
      </div>
    )
  }

  return (
    <>
      {loading && (
        <div style={{ background: '#f8f8f8', height: '100%', width: '100%' }}>
          <LoadingIndicator />
        </div>
      )}
      <ForgeViewer
        position={position}
        viewerPosition={viewerPosition}
        viewerPositionDelta={viewerPositionDelta}
        followRealtime={followRealtime}
        fileUrn={buildingState.projectData?.forge_model_urn}
        sync={imageViewerContext.sync}
        initialRotation={rotation}
        onDragging={onDragging}
        onUpdate={onUpdate}
        onLoaded={handleLoaded}
        projectId={buildingState.projectId}
        onRequestCameraScreenshot={onRequestCameraScreenshot}
      />
    </>
  )
}
